/** Subscription pricing utilities — aligned with BeautyAppFront SubscriptionManagement + currency.js */

const EU_COUNTRY_CODES = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT',
  'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'SK', 'SI', 'ES', 'SE',
]);

export const PLAN_CURRENCIES = ['ILS', 'USD', 'RON', 'EUR'];

const LANGUAGE_TO_PLAN_CURRENCY = { he: 'ILS', ar: 'ILS', en: 'USD', ru: 'USD' };

export function getPreferredPlanCurrencyForPlans(countryCode, appLanguage) {
  const code = (countryCode || '').toUpperCase().slice(0, 2);
  if (code === 'IL') return 'ILS';
  if (code === 'RO') return 'RON';
  if (EU_COUNTRY_CODES.has(code)) return 'EUR';
  if (code) return 'USD';
  const lang = (appLanguage || '').toLowerCase().slice(0, 2);
  return LANGUAGE_TO_PLAN_CURRENCY[lang] || 'USD';
}

/** Parse subscription-plans API response (supports legacy array or meta envelope). */
export function parseSubscriptionPlansResponse(data, appLanguage) {
  if (Array.isArray(data)) {
    return {
      plans: data,
      currency: getPreferredPlanCurrencyForPlans(null, appLanguage),
      detectedCountry: null,
    };
  }
  const plans = Array.isArray(data?.plans) ? data.plans : [];
  const currency = data?.currency
    || getPreferredPlanCurrencyForPlans(data?.detected_country, appLanguage);
  return {
    plans,
    currency,
    detectedCountry: data?.detected_country || null,
  };
}

export const PLAN_NAME_TO_KEY = {
  base: 'base',
  basic: 'base',
  premium: 'base',
  enterprise: 'enterprise',
  בסיסי: 'basic',
  מקצועי: 'premium',
  פרימיום: 'premium',
  ארגוני: 'enterprise',
  أساسي: 'basic',
  احترافي: 'premium',
  بريميوم: 'premium',
  مؤسسي: 'enterprise',
  базовый: 'basic',
  профессиональный: 'premium',
  премиум: 'premium',
  корпоративный: 'enterprise',
};

export function getPlanKey(apiName) {
  if (!apiName || typeof apiName !== 'string') return null;
  const trimmed = apiName.trim();
  const lower = trimmed.toLowerCase();
  return PLAN_NAME_TO_KEY[lower] || PLAN_NAME_TO_KEY[trimmed] || null;
}

function inferPlanGroupSlug(name) {
  if (!name) return '';
  return name.trim().toLowerCase();
}

/** Merge API rows that share product + interval so all currency tiers live on one card (app parity). */
export function mergePlansForMultiCurrencyDisplay(plansInput) {
  const arr = Array.isArray(plansInput) ? plansInput : [];
  const normalized = arr.map((p) => {
    if (Array.isArray(p.prices) && p.prices.length > 0) return p;
    const cur = (p.currency || 'USD').toUpperCase();
    const amt = p.price != null ? p.price : p.amount;
    return {
      ...p,
      prices: [{ currency: cur, amount: String(amt), stripe_price_id: p.id }],
    };
  });

  const groups = new Map();
  for (const p of normalized) {
    const planKey = getPlanKey(p.name);
    const productId = p.product_id ?? p.productId;
    const slug = inferPlanGroupSlug(p.name);
    const key = productId
      ? `${String(productId)}|${p.interval || ''}`
      : planKey
        ? `${planKey}|${p.interval || ''}`
        : slug
          ? `${slug}|${p.interval || ''}`
          : String(p.id);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(p);
  }

  const out = [];
  for (const variants of groups.values()) {
    const allPriceIds = [...new Set(variants.map((v) => v.id).filter(Boolean))];
    if (variants.length === 1) {
      out.push({ ...variants[0], _allPriceIds: allPriceIds });
      continue;
    }
    const byCur = new Map();
    for (const pl of variants) {
      const tiers =
        Array.isArray(pl.prices) && pl.prices.length > 0
          ? pl.prices
          : [{ currency: pl.currency, amount: pl.price, stripe_price_id: pl.id }];
      for (const tier of tiers) {
        const c = (tier.currency || '').toUpperCase();
        if (!c) continue;
        const sid = tier.stripe_price_id || tier.stripePriceId || pl.id;
        const amt = tier.amount != null ? tier.amount : tier.price;
        if (!byCur.has(c)) byCur.set(c, { amount: amt, stripe_price_id: sid });
      }
    }
    const base = variants.reduce((best, cur) => {
      const n = Array.isArray(cur.prices) ? cur.prices.length : 0;
      const bn = Array.isArray(best.prices) ? best.prices.length : 0;
      return n > bn ? cur : best;
    }, variants[0]);
    const prices = [...byCur.entries()].map(([currency, v]) => ({
      currency,
      amount: String(v.amount),
      stripe_price_id: v.stripe_price_id,
    }));
    out.push({
      ...base,
      prices,
      id: prices[0]?.stripe_price_id || base.id,
      _allPriceIds: allPriceIds,
    });
  }
  return out;
}

export function pickPlanPriceForDisplay(plan, preferredCurrencyCode) {
  const preferred = (preferredCurrencyCode || '').toUpperCase();
  const raw = plan?.prices;
  const tiers = Array.isArray(raw) && raw.length > 0 ? raw : null;
  const toNum = (v) => {
    const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
  };
  const tierStripeId = (row) => row?.stripe_price_id || row?.stripePriceId || plan?.id;
  if (tiers) {
    const find = (code) => tiers.find((p) => (p.currency || '').toUpperCase() === code);
    if (preferred) {
      const hit = find(preferred);
      if (hit) {
        return {
          amount: toNum(hit.amount),
          currency: (hit.currency || preferred).toUpperCase(),
          stripePriceId: tierStripeId(hit),
        };
      }
    }
    const usd = find('USD');
    if (usd) {
      return { amount: toNum(usd.amount), currency: 'USD', stripePriceId: tierStripeId(usd) };
    }
    const first = tiers[0];
    return {
      amount: toNum(first.amount),
      currency: (first.currency || 'USD').toUpperCase(),
      stripePriceId: tierStripeId(first),
    };
  }
  return {
    amount: toNum(plan?.price),
    currency: (plan?.currency || 'USD').toUpperCase(),
    stripePriceId: plan?.id,
  };
}

export const PLAN_ORDER = ['base', 'enterprise'];

const SHARED_PLAN_FEATURES = [
  'planFeatureSupport',
  'planFeatureUnlimitedAppointments',
  'planFeatureNotificationsToClients',
  'planFeatureGoogleCalendarSync',
  'planFeatureEarningReports',
  'planFeatureInvoicesReceipts',
  'planFeatureWaitingList',
  'planFeatureWhatsAppNotifications',
  'planFeatureOneSeatIncluded',
  'planFeatureExtraSeats',
  'planFeatureProratedSeats',
];

export const PLAN_FEATURES = {
  base: SHARED_PLAN_FEATURES,
  basic: SHARED_PLAN_FEATURES,
  premium: SHARED_PLAN_FEATURES,
  enterprise: [
    'planFeatureSupport',
    'planFeatureUnlimitedAppointments',
    'planFeatureNotificationsToClients',
    'planFeatureGoogleCalendarSync',
    'planFeatureEarningReports',
    'planFeatureInvoicesReceipts',
    'planFeatureWaitingList',
    'planFeatureWhatsAppNotifications',
    'planFeatureTwentyPlus',
  ],
};

export const MANUAL_ENTERPRISE = {
  planKey: 'enterprise',
  name: 'Enterprise',
  isManualEnterprise: true,
};

export function pickSeatPriceForDisplay(plan, preferredCurrency) {
  if (Array.isArray(plan?.seat_prices) && plan.seat_prices.length > 0) {
    return pickPlanPriceForDisplay(
      { ...plan, prices: plan.seat_prices, price: plan.seat_price, id: plan.seat_price_id },
      preferredCurrency,
    );
  }
  if (plan?.seat_price == null) return null;
  const n = typeof plan.seat_price === 'number' ? plan.seat_price : parseFloat(plan.seat_price);
  return {
    amount: Number.isFinite(n) ? n : 0,
    currency: (plan.seat_currency || plan.currency || 'USD').toUpperCase(),
  };
}

/** Build tier cards for the selected billing interval + locale currency. */
export function buildPricingTiers(mergedPlans, interval, preferredCurrency) {
  const iv = (interval || 'month').toLowerCase();
  const tiers = [];
  const commercials = (mergedPlans || []).filter((p) => {
    const key = getPlanKey(p.name);
    return (p.interval || 'month').toLowerCase() === iv && key !== 'enterprise';
  });
  const plan = commercials.find((p) => p.seat_price != null || p.seat_price_id) || commercials[0];
  if (plan) {
    tiers.push({
      planKey: 'base',
      name: plan.name,
      interval: plan.interval,
      display: pickPlanPriceForDisplay(plan, preferredCurrency),
      seatDisplay: pickSeatPriceForDisplay(plan, preferredCurrency),
      monthly_equivalent: plan.monthly_equivalent,
      isManualEnterprise: false,
    });
  }

  tiers.push({ ...MANUAL_ENTERPRISE });
  return tiers;
}

const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', ILS: '₪', RON: 'lei',
};

export function getCurrencySymbol(currency) {
  return CURRENCY_SYMBOLS[(currency || 'USD').toUpperCase()] || currency;
}

export function formatPlanPrice(amount, currency, interval, t) {
  const symbol = getCurrencySymbol(currency);
  const per = interval === 'year' ? t('perYear') : t('perMonth');
  const num = typeof amount === 'number' ? amount : parseFloat(amount);
  const formatted = Number.isFinite(num) ? (Number.isInteger(num) ? num : num.toFixed(2)) : amount;
  return `${formatted} ${symbol}/${per}`;
}

export function getMonthlyEquivalent(plan, displayAmount) {
  if (plan.interval !== 'year') return null;
  if (plan.monthly_equivalent != null) {
    const n = parseFloat(plan.monthly_equivalent);
    if (Number.isFinite(n)) return n;
  }
  return Number.isFinite(displayAmount) ? Math.round((displayAmount / 12) * 100) / 100 : null;
}
